import React from 'react';

import {
  type PatientImportEditableField,
  type PatientImportIssueCategory,
  type PatientImportResolveKind,
  type PatientImportRowDedupeStatus,
  type PatientImportRowResolution,
} from '@eluve/api-contract';
import { InlineEdit } from '@eluve/blocks';
import {
  Button,
  Caption,
  Icon,
  type IconName,
  Stack,
  Tag,
  type TagProps,
  Text,
  cn,
  tv,
} from '@eluve/components';

import { useImportRowMutations } from '../hooks/useImportRowMutations';
import { displayValueForField, formatDobForDisplay } from '../utils';

export type ImportRow = {
  id: string;
  rowNumber: number;
  patientId: string | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  email: string | null;
  phoneNumber: string | null;
  dedupeStatus: PatientImportRowDedupeStatus;
  resolution: PatientImportRowResolution | null;
};

export type ImportIssue = {
  id: string;
  category: PatientImportIssueCategory;
  blocking: boolean;
  message: string;
  field: string | null;
  metadata: Record<string, unknown> | null;
};

type ResolveOption = {
  kind: PatientImportResolveKind;
  label: string;
  icon?: IconName;
  primary?: boolean;
};

const skipRow: ResolveOption = {
  kind: 'SKIPPED',
  label: 'Skip row',
  icon: 'X',
};
const removeRow: ResolveOption = {
  kind: 'REMOVED',
  label: 'Remove this row',
  icon: 'Check',
};
const keepOld: ResolveOption = {
  kind: 'KEPT_OLD',
  label: 'Keep existing record',
};
const overwrite: ResolveOption = {
  kind: 'OVERWRITE',
  label: 'Use this record',
  primary: true,
};
const keepBoth: ResolveOption = {
  kind: 'KEEP_BOTH',
  label: 'Keep both records',
};
const updateUpload: ResolveOption = {
  kind: 'UPDATE_UPLOAD',
  label: 'Match existing record',
};

export type { ResolveOption };

export const RESOLVE_OPTIONS: Record<
  PatientImportRowDedupeStatus,
  ResolveOption[]
> = {
  PENDING: [skipRow],
  NEW: [skipRow],
  DUPE_ID_EXACT_MATCH: [],
  DUPE_ID_SAME_PATIENT: [overwrite, keepOld, skipRow],
  DUPE_ID_DIFFERENT_PATIENT: [updateUpload, keepOld, skipRow],
  DUPE_IN_FILE: [removeRow, skipRow],
  PATIENT_EXISTS_NO_ID_MATCH: [keepOld, keepBoth, skipRow],
};

type TagColor = TagProps['color'];

const RESOLUTION_PILL: Record<
  PatientImportRowResolution,
  { label: string; color: TagColor }
> = {
  FIXED: { label: 'Fixed', color: 'positive' },
  OVERWRITE: { label: 'Will overwrite existing', color: 'positive' },
  KEEP_BOTH: { label: 'Both records will be kept', color: 'positive' },
  UPDATE_UPLOAD: { label: 'Upload matched to existing', color: 'positive' },
  REMOVED: { label: 'Duplicate removed', color: 'positive' },
  SKIPPED: { label: 'Skipped', color: 'gray' },
  KEPT_OLD: { label: 'Kept existing · upload discarded', color: 'gray' },
};

const isSkippedResolution = (
  resolution: PatientImportRowResolution | null,
): boolean => resolution === 'SKIPPED' || resolution === 'KEPT_OLD';

const rowContainerStyles = tv({
  base: 'border-b border-border-tertiary px-4 py-3 transition-colors last:border-b-0',
  variants: {
    state: {
      unresolved: 'bg-bg-primary',
      skipped: 'bg-bg-secondary',
      resolved: 'bg-bg-positive-subtle',
    },
  },
});

export const FIELDS: {
  key: keyof ImportRow;
  field: PatientImportEditableField;
  label: string;
}[] = [
  { key: 'patientId', field: 'patient_id', label: 'patient_id' },
  { key: 'firstName', field: 'first_name', label: 'first_name' },
  { key: 'lastName', field: 'last_name', label: 'last_name' },
  { key: 'dateOfBirth', field: 'date_of_birth', label: 'date_of_birth' },
  { key: 'email', field: 'email', label: 'email' },
  { key: 'phoneNumber', field: 'phone_number', label: 'phone_number' },
];

export const ResolvedPill: React.FC<{
  resolution: PatientImportRowResolution;
}> = ({ resolution }) => {
  const { label, color } = RESOLUTION_PILL[resolution];
  return <Tag size="xs" color={color} label={label} />;
};

export const FieldEditor: React.FC<{
  value: string;
  blocking: boolean;
  errorMessage?: string;
  placeholder?: string;
  onSave: (next: string | null) => void | Promise<void>;
}> = ({ value, blocking, errorMessage, placeholder, onSave }) => (
  <Stack gap={1} className="min-w-0">
    <InlineEdit
      value={value}
      onSave={async (next) => {
        await onSave(next === '' ? null : next);
      }}
      className={
        blocking
          ? 'rounded-md border border-dashed border-border-negative-subtle bg-bg-negative-subtle px-2 py-1'
          : 'rounded-md px-2 py-1'
      }
      placeholder={placeholder}
    >
      <Text size="sm" className="font-mono break-all">
        {value === '' ? <span className="text-text-disabled">—</span> : value}
      </Text>
    </InlineEdit>
    {errorMessage ? (
      <Text size="xs" className="text-text-negative">
        {errorMessage}
      </Text>
    ) : null}
  </Stack>
);

export const FieldCell: React.FC<{
  label: string;
  value: string;
  blocking: boolean;
  errorMessage?: string;
  onSave: (next: string | null) => void | Promise<void>;
}> = ({ label, value, blocking, errorMessage, onSave }) => (
  <Stack gap={1} className="min-w-0">
    <Caption color="tertiary" size="2xs" className="font-mono normal-case">
      {label}
    </Caption>
    <FieldEditor
      value={value}
      blocking={blocking}
      errorMessage={errorMessage}
      placeholder={label}
      onSave={onSave}
    />
  </Stack>
);

export const RowCard: React.FC<{
  row: ImportRow;
  issues: ImportIssue[];
  tenantId: string;
  jobId: string;
  onMutationSuccess?: () => void | Promise<void>;
}> = ({ row, issues, tenantId, jobId, onMutationSuccess }) => {
  const { updateField, resolveRow, unresolveRow } = useImportRowMutations(
    tenantId,
    jobId,
    onMutationSuccess,
  );

  const resolved = row.resolution !== null;
  const skipped = isSkippedResolution(row.resolution);
  const resolveOptions = RESOLVE_OPTIONS[row.dedupeStatus];
  const blockingByField = new Map<string, string>();
  for (const issue of issues) {
    if (issue.blocking && issue.field && !blockingByField.has(issue.field)) {
      blockingByField.set(issue.field, issue.message);
    }
  }
  const statusNote = !resolved ? issues[0]?.message : null;
  const namePreview =
    [row.firstName, row.lastName].filter(Boolean).join(' ') || '—';

  const containerState = !resolved
    ? 'unresolved'
    : skipped
      ? 'skipped'
      : 'resolved';
  const strikeClass = skipped ? 'line-through opacity-60' : '';
  const existing = extractExistingPatient(row.dedupeStatus, issues);

  return (
    <div className={rowContainerStyles({ state: containerState })}>
      <Stack gap={3}>
        <Stack direction="horizontal" justify="between" align="start" gap={3}>
          <Stack
            direction="horizontal"
            gap={2}
            align="center"
            className="min-w-0 flex-wrap"
          >
            <Caption color="tertiary" size="2xs" className="font-mono">
              Row {row.rowNumber}
            </Caption>
            <Text inline size="sm" color="secondary">
              {namePreview}
            </Text>
            {statusNote ? (
              <Text inline size="xs" color="tertiary">
                · {statusNote}
              </Text>
            ) : null}
            {row.resolution ? (
              <ResolvedPill resolution={row.resolution} />
            ) : null}
          </Stack>
          <Stack direction="horizontal" gap={2} fullWidth={false}>
            {resolved ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => unresolveRow(row.id)}
              >
                <Icon name="RefreshCw" size="auto" />
                Undo
              </Button>
            ) : (
              resolveOptions.map((opt) => (
                <Button
                  key={opt.kind}
                  size="sm"
                  variant={opt.primary ? 'default' : 'outline'}
                  onClick={() => resolveRow(row.id, opt.kind)}
                >
                  {opt.icon ? <Icon name={opt.icon} size="auto" /> : null}
                  {opt.label}
                </Button>
              ))
            )}
          </Stack>
        </Stack>

        {existing ? (
          <div className="grid grid-cols-[7rem_repeat(6,1fr)] gap-x-2 gap-y-2">
            <div />
            {FIELDS.map(({ field, label }) => (
              <Caption
                key={field}
                color="tertiary"
                size="2xs"
                className="font-mono normal-case"
              >
                {label}
              </Caption>
            ))}

            <RowLabelTag label="Your upload" />
            {FIELDS.map(({ key, field, label }) => (
              <FieldEditor
                key={field}
                value={displayValueForField(
                  field,
                  (row[key] as string | null) ?? null,
                )}
                blocking={blockingByField.has(field)}
                errorMessage={blockingByField.get(field)}
                placeholder={label}
                onSave={async (next) => {
                  await updateField(row.id, field, next);
                }}
              />
            ))}

            <RowLabelTag label="Existing in Eluve" />
            <ExistingValue value={row.patientId} />
            <ExistingValue value={existing.firstName} />
            <ExistingValue value={existing.lastName} />
            <ExistingValue value={formatExistingDob(existing.dateOfBirth)} />
            <ExistingValue value={null} />
            <ExistingValue value={null} />
          </div>
        ) : (
          <div className={cn('grid grid-cols-6 gap-2', strikeClass)}>
            {FIELDS.map(({ key, field, label }) => (
              <FieldCell
                key={field}
                label={label}
                value={displayValueForField(
                  field,
                  (row[key] as string | null) ?? null,
                )}
                blocking={blockingByField.has(field)}
                errorMessage={blockingByField.get(field)}
                onSave={async (next) => {
                  await updateField(row.id, field, next);
                }}
              />
            ))}
          </div>
        )}
      </Stack>
    </div>
  );
};

const RowLabelTag: React.FC<{ label: string }> = ({ label }) => (
  <Tag size="xs" color="gray" label={label} className="mt-1.5 w-fit" />
);

const ExistingValue: React.FC<{ value: string | null }> = ({ value }) => (
  <Text
    size="sm"
    color={value ? 'primary' : 'disabled'}
    className="px-2 py-1 font-mono break-all"
  >
    {value ?? '—'}
  </Text>
);

const formatExistingDob = (raw: string | null): string | null => {
  const formatted = formatDobForDisplay(raw);
  return formatted === '' ? null : formatted;
};

const asString = (v: unknown): string | null =>
  typeof v === 'string' ? v : null;

const extractExistingPatient = (
  dedupeStatus: PatientImportRowDedupeStatus,
  issues: ImportIssue[],
): {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
} | null => {
  if (
    dedupeStatus !== 'DUPE_ID_DIFFERENT_PATIENT' &&
    dedupeStatus !== 'DUPE_ID_SAME_PATIENT'
  ) {
    return null;
  }
  for (const issue of issues) {
    const meta = issue.metadata;
    if (!meta) continue;
    const firstName = asString(meta['existingFirstName']);
    const lastName = asString(meta['existingLastName']);
    const dateOfBirth = asString(meta['existingDateOfBirth']);
    if (firstName !== null || lastName !== null || dateOfBirth !== null) {
      return { firstName, lastName, dateOfBirth };
    }
  }
  return null;
};
